import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, CalendarPlus } from 'lucide-react';
import { Pet } from '../../types/pet';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useCachedPhotoUrl } from '../../hooks/useCachedPhotoUrl';
import '../../utils/authUtils';

interface PetOwnerInfo {
  id: string;
  user_id?: string;
  full_name: string;
}

interface PetsListProps {
  ownerUuid: string;
  showHeader?: boolean;
  maxItems?: number;
  petOwner?: PetOwnerInfo;
  onBookAppointment?: (pet: Pet) => void;
}

const PetsList: React.FC<PetsListProps> = ({
  ownerUuid,
  showHeader = true,
  maxItems,
  petOwner,
  onBookAppointment
}) => {
  const [pets, setPets] = useState<Pet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchPets();
  }, [ownerUuid]);

  const fetchPets = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.PETS.BY_OWNER(ownerUuid));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied to view pets for this owner');
        }
        throw new Error('Failed to fetch pets');
      }

      const petsData = await response.json();
      setPets(petsData);
    } catch (err) {
      console.error('Error fetching pets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pets');
    } finally {
      setLoading(false);
    }
  };

  const canCreatePets = () => {
    return user?.role === 'ADMIN' || user?.role === 'VET_STAFF';
  };

  const canBookAppointments = () => {
    return user?.role === 'ADMIN' || user?.role === 'VET_STAFF' || user?.role === 'PRACTICE_ADMIN';
  };

  const displayedPets = maxItems ? pets.slice(0, maxItems) : pets;

  // Separate component for pet card to properly use hooks
  const PetCard: React.FC<{ pet: Pet }> = ({ pet }) => {
    const cachedPhotoUrl = useCachedPhotoUrl(pet.id, pet.photo_url);

    const handleBookAppointment = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onBookAppointment) {
        onBookAppointment(pet);
      }
    };

    return (
      <Link
        to={`/dashboard/pets/${pet.id}`}
        className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-blue-400 transition-all duration-200 block group"
      >
        {/* Pet Photo - Large and Prominent */}
        <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
          {cachedPhotoUrl ? (
            <img
              src={cachedPhotoUrl}
              alt={pet.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className="w-16 h-16 text-gray-300" />
            </div>
          )}
          {!pet.is_active && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-red-500 text-white rounded-full shadow-lg">
                Inactive
              </span>
            </div>
          )}
        </div>

        {/* Pet Info */}
        <div className="p-4">
          <div className="mb-3">
            <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-1">
              {pet.name}
            </h4>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="font-medium">{pet.species}</span>
              {pet.breed && (
                <>
                  <span className="text-gray-400">•</span>
                  <span>{pet.breed}</span>
                </>
              )}
            </div>
          </div>

          {/* Book Appointment Button - Facebook style */}
          {canBookAppointments() && onBookAppointment && (
            <button
              onClick={handleBookAppointment}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg border border-blue-200 transition-colors duration-200"
            >
              <CalendarPlus className="w-5 h-5" />
              <span>Book Appointment</span>
            </button>
          )}
        </div>
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading pets...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Heart className="w-5 h-5 mr-2 text-blue-600" />
            Pets {pets.length > 0 && `(${pets.length})`}
          </h3>
          {canCreatePets() && (
            <Link
              to={`/pets/create?owner_id=${ownerUuid}`}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
            >
              Add Pet
            </Link>
          )}
        </div>
      )}

      {pets.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Heart className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No pets registered</h3>
          <p className="text-gray-600 mb-4">
            This pet owner doesn't have any pets registered yet.
          </p>
          {canCreatePets() && (
            <Link
              to={`/pets/create?owner_id=${ownerUuid}`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
            >
              Add First Pet
            </Link>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {displayedPets.map((pet) => (
            <PetCard key={pet.id} pet={pet} />
          ))}
        </div>
      )}

      {maxItems && pets.length > maxItems && (
        <div className="text-center pt-4">
          <Link
            to={`/pet_owners/${ownerUuid}/pets`}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            View all {pets.length} pets →
          </Link>
        </div>
      )}
    </div>
  );
};

export default PetsList;
